import { FC, ReactNode } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'next-i18next';
import { Form } from 'antd';

import { FieldContainerIw } from '@/_pages/interwill/components/FieldContainerIw';
import { useBeneficiariesActions } from '@/entities/beneficiary';
import { SelectBeneficiaryGroup } from '@/entities/beneficiary/components/SelectFieldBeneficiaryGroup';
import { useBeneficiariesPidActions } from '@/entities/beneficiary/hooks/useBeneficiariesPidActions';
import { Input } from '@/shared/components';
import { EMAIL_REGEX } from '@/shared/lib/hooks/use-form-render/helpers';
import { BeneficiaryDto } from '@/shared/lib/sj-orm/models/beneficiary.dto';
import { DocumentType } from '@/shared/lib/sj-orm/models/document/document-base.dto';
import { useModalsStore } from '@/widgets/modals/modals.store';

import {
  InterwillFieldsLineWrapper,
  InterwillFormContainer,
  InterwillStyledForm,
  InterwillSubmitButtonWrapper,
  // TODO rework
} from '../CreateEntityModal/styled';

export type FormData = Pick<
  BeneficiaryDto,
  'nickName' | 'firstName' | 'secondName' | 'lastName' | 'group' | 'email'
>;

type FormProps = {
  form: UseFormReturn<FormData>;
  submitBtn?: ReactNode;
  description?: ReactNode;
  onSubmit: (data: BeneficiaryDto) => void;
};

export const CreateForm: FC<FormProps> = ({ form, submitBtn, description, onSubmit }) => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'createBeneficiary' });
  const { create } = useBeneficiariesActions();
  const { create: createPid } = useBeneficiariesPidActions();
  const { control, handleSubmit: formHandleSubmit } = form;
  const modals = useModalsStore();

  const handleSubmit = async (data: FormData) => {
    const res = await create({
      ...data,
      linkedCountryCode: '',
    });

    if (res !== null) {
      createPid({
        beneficiaryDtoId: res.id,
        type: DocumentType.BENEFICIARY_PERSONAL_DATA,
        lastNameInNativeLanguage: '',
        nameInNativeLanguage: '',
      });
      onSubmit(res);
      modals.closeModal();
    } else {
      console.error('[CreateBeneficiaryModal] Create Beneficiary error');
    }
  };

  return (
    <>
      <InterwillStyledForm onFinish={formHandleSubmit(handleSubmit)}>
        <InterwillFormContainer>
          {description}

          <InterwillFieldsLineWrapper>
            <Controller
              name={'nickName'}
              control={control}
              rules={{ required: t('fields.nickName.error') }}
              render={({ field, fieldState }) => {
                return (
                  <FieldContainerIw required label={t(`fields.${field.name}.label`)}>
                    <Form.Item
                      validateStatus={fieldState.error ? 'error' : ''}
                      help={fieldState.error?.message}
                    >
                      <Input
                        placeholder={t(`fields.${field.name}.placeholder`)}
                        type={'text'}
                        {...field}
                      />
                    </Form.Item>
                  </FieldContainerIw>
                );
              }}
            />

            <Controller
              name={'firstName'}
              control={control}
              rules={{ required: t('fields.firstName.error') }}
              render={({ field, fieldState }) => {
                return (
                  <FieldContainerIw required label={t(`fields.${field.name}.label`)}>
                    <Form.Item
                      validateStatus={fieldState.error ? 'error' : ''}
                      help={fieldState.error?.message}
                    >
                      <Input
                        placeholder={t(`fields.${field.name}.placeholder`)}
                        type={'text'}
                        {...field}
                      />
                    </Form.Item>
                  </FieldContainerIw>
                );
              }}
            />
          </InterwillFieldsLineWrapper>

          <InterwillFieldsLineWrapper>
            <Controller
              name={'secondName'}
              control={control}
              rules={{ required: false }}
              render={({ field, fieldState }) => {
                return (
                  <FieldContainerIw label={t(`fields.${field.name}.label`)}>
                    <Form.Item
                      validateStatus={fieldState.error ? 'error' : ''}
                      help={fieldState.error?.message}
                    >
                      <Input
                        placeholder={t(`fields.${field.name}.placeholder`)}
                        type={'text'}
                        {...field}
                      />
                    </Form.Item>
                  </FieldContainerIw>
                );
              }}
            />
            <Controller
              name={'lastName'}
              control={control}
              rules={{ required: t('fields.lastName.error') }}
              render={({ field, fieldState }) => {
                return (
                  <FieldContainerIw required label={t(`fields.${field.name}.label`)}>
                    <Form.Item
                      validateStatus={fieldState.error ? 'error' : ''}
                      help={fieldState.error?.message}
                    >
                      <Input
                        placeholder={t(`fields.${field.name}.placeholder`)}
                        type={'text'}
                        {...field}
                      />
                    </Form.Item>
                  </FieldContainerIw>
                );
              }}
            />
          </InterwillFieldsLineWrapper>

          <Controller
            name={'group'}
            control={control}
            rules={{ required: t('fields.group.error') }}
            render={({ field, fieldState }) => {
              return (
                <FieldContainerIw required label={t(`fields.${field.name}.label`)}>
                  <Form.Item
                    validateStatus={fieldState.error ? 'error' : ''}
                    help={fieldState.error?.message}
                  >
                    <SelectBeneficiaryGroup
                      placeholder={t(`fields.${field.name}.placeholder`)}
                      {...field}
                    />
                  </Form.Item>
                </FieldContainerIw>
              );
            }}
          />

          <Controller
            name={'email'}
            control={control}
            rules={{
              pattern: {
                value: EMAIL_REGEX,
                message: t('fields.email.incorrectEmailError'),
              },
              required: t('fields.email.error'),
            }}
            render={({ field, fieldState }) => {
              return (
                <FieldContainerIw required label={t(`fields.${field.name}.label`)}>
                  <Form.Item
                    validateStatus={fieldState.error ? 'error' : ''}
                    help={fieldState.error?.message}
                  >
                    <Input
                      placeholder={t(`fields.${field.name}.placeholder`)}
                      type={'text'}
                      {...field}
                    />
                  </Form.Item>
                </FieldContainerIw>
              );
            }}
          />
        </InterwillFormContainer>

        <InterwillSubmitButtonWrapper>{submitBtn}</InterwillSubmitButtonWrapper>
      </InterwillStyledForm>
    </>
  );
};
